import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Form } from 'react-bootstrap'
import InputMask from 'react-input-mask'
import { v4 as uuidv4 } from 'uuid'
import classNames from 'classnames'

import { Icon, IconType } from 'components/atoms/Icon'
import CalendarForInput from 'components/molecules/CalendarForInput'

import { getDateFromServerDate, getDateTimeForInput, getDateTimeForServer } from 'utils/date'

import onPasteDateTime from './utils/onPasteDateTime'

import classes from './style.module.css'

const PLACEHOLDER_DATETIME = '00.00.0000 00:00'

export interface InputControlProps {
  id: string
  value: string
  isValid?: boolean | undefined
  onChangeHandler(value: string, id?: string): void
  className?: string
  label?: string
  invalidMessage?: string
  required?: boolean
}

const getStringForJSDATEFromDateTime = (date: string) => {
  const arrayMatch = date.match(/^([0-9]{2})[.]([0-9]{2})[.]([0-9]{4}) ([0-9]{2}):([0-9]{2})?$/)
  if (arrayMatch) {
    return `${arrayMatch[3]}-${arrayMatch[2]}-${arrayMatch[1]}T${arrayMatch[4]}:${arrayMatch[5]}`
  }
  return ''
}

const checkThatTwoDigits = (value: number) => {
  // TODO Take out
  if (Number(value) < 10) {
    return `0${value}`
  }
  return value
}

const isDateTimeValid = (date: string) => {
  const [, day, month, year, hour, minute] = date.match(/^([0-9]{2})[.]([0-9]{2})[.]([0-9]{4}) ([0-9]{2}):([0-9]{2})?$/)
  const tempDate = new Date(Number(year), Number(month) - 1, Number(day), Number(hour), Number(minute))
  return (
    date.length === 16 &&
    Number(year) >= 1920 &&
    Number(year) <= 2100 &&
    String(tempDate.getFullYear()) === year &&
    String(checkThatTwoDigits(tempDate.getMonth() + 1)) === month &&
    String(checkThatTwoDigits(tempDate.getDate())) === day &&
    String(checkThatTwoDigits(tempDate.getHours())) === hour &&
    String(checkThatTwoDigits(tempDate.getMinutes())) === minute
  )
}

export const InputDateTime: React.FC<InputControlProps> = ({
  id,
  value,
  isValid,
  onChangeHandler,
  className = '',
  label,
  invalidMessage = '',
  required = false,
}) => {
  const [val, setVal] = useState(() => {
    if (value.length === 16 && value.match(/^([0-9]{4}).([0-9]{2}).([0-9]{2}) ([0-9]{2}):([0-9]{2})?$/)) {
      return getDateTimeForInput(new Date(value))
    }
    return value
  })

  const [isCalendarVisible, setIsCalendarVisible] = useState(false)
  const inputMaskRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    // eslint-disable-next-line no-restricted-globals
    if (value.length === 16 && value.match(/^([0-9]{4}).([0-9]{2}).([0-9]{2}) ([0-9]{2}):([0-9]{2})?$/)) {
      setVal(getDateTimeForInput(getDateFromServerDate(value)))
    } else {
      setVal(value)
    }
  }, [value, setVal])

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const changedValue = e.target.value
      setVal(changedValue)
      if (isDateTimeValid(changedValue)) {
        onChangeHandler(getDateTimeForServer(new Date(getStringForJSDATEFromDateTime(changedValue))), id)
      } else {
        onChangeHandler(changedValue, id)
      }
    },
    [onChangeHandler, id, setVal],
  )

  const CalendarVisibilityHandler = useCallback(() => {
    setIsCalendarVisible((prev) => {
      if (!prev) document.getElementById(id)!.focus() // Ref not work :( // TODO fix it
      return !prev
    })
    // inputMaskRef.current!.focus()
  }, [setIsCalendarVisible, id])

  const CalendarOnChangeHandler = useCallback(
    (date: Date) => {
      if (date.getFullYear() >= 1980 && date.getFullYear() <= 2100) onChangeHandler(getDateTimeForServer(date) || '', id)
      else onChangeHandler(getDateTimeForInput(date) || '', id)
    },
    [onChangeHandler, id],
  )

  const stopPropagation = useCallback((event) => {
    event.preventDefault()
    event.stopPropagation()
    return false
  }, [])

  const HandleBlur = useCallback(() => {
    setIsCalendarVisible(false)
    if (val === PLACEHOLDER_DATETIME) onChangeHandler('', id)
  }, [setIsCalendarVisible, val, id, onChangeHandler])

  // eslint-disable-next-line no-nested-ternary
  const validClass = isValid === false ? 'is-invalid' : isValid === true ? 'is-valid' : ''

  const name = useMemo(() => uuidv4(), [])

  const calendarValue = value && value !== PLACEHOLDER_DATETIME ? value : getDateTimeForServer(new Date()) || ''

  return (
    <Form.Group className="m-0">
      {label ? <Form.Label>{label}</Form.Label> : null}
      {label ? <br /> : null}
      <div className={classNames(classes.container, isValid === undefined && classes.containerWithoutValidation, className)}>
        <InputMask
          id={id}
          className={classNames('inputMask form-control datetime', validClass)}
          value={val || ''}
          mask="99.99.9999 99:99"
          maskChar="0"
          onChange={onChange}
          onPaste={(event: React.ClipboardEvent<HTMLInputElement>) => {
            onPasteDateTime(event, onChangeHandler)
          }}
          required={required}
          autoComplete="none"
          name={name}
          inputRef={inputMaskRef}
          onBlur={HandleBlur}
        />

        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
        <span className={classes.calendarIcon} onClick={CalendarVisibilityHandler} onMouseDown={stopPropagation}>
          <Icon type={IconType.Calendar} width="30px" height="30px" />
        </span>
      </div>

      {isCalendarVisible ? (
        // eslint-disable-next-line jsx-a11y/no-static-element-interactions,jsx-a11y/click-events-have-key-events
        <div className={classes.container_calendar} onClick={() => setIsCalendarVisible(false)}>
          {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions,jsx-a11y/click-events-have-key-events */}
          <div className={classes.calendar} onMouseDown={stopPropagation} onClick={stopPropagation}>
            <CalendarForInput onChange={CalendarOnChangeHandler} value={calendarValue} withTime />
          </div>
        </div>
      ) : null}

      {invalidMessage && isValid === false ? (
        <div className="invalid-feedback" style={{ display: 'block' }}>
          {invalidMessage}
        </div>
      ) : null}
    </Form.Group>
  )
}

export default InputDateTime
